import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshProjectProps } from "../../types/screensTypes";
import { getCartelAIWaiting, createCartelRemixQueueAI, createProjectCartel, addItemToProjectAI } from "../../api";
import AddCartelToProjectModal from "../../components/modals/AddCartelToProjectModal";
import AddToProjectButton from "../../components/ui/AddToProjectButton";
import { FieldErrorsRemix } from "../../types/types";
import Button from "../../components/ui/Button";
import { useGlobalState } from "../../context/useGlobalState";
import AIGenerationStatusModal from "../../components/modals/AIGenerationStatusModal";
import { AI_MODEL_TYPES } from "../../constants";
import { Tooltip } from "@mui/material";
import { useRemainingTokens } from "../../hooks/useRemainingTokens";
import { isQuotaError } from "../../utils/quotaError";
import Icon from "../../components/common/Icon";
import styles from "./TextRemix.module.css";

const CartelRemix: React.FC<RefreshProjectProps> = ({ refreshProjects }) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    const [textValue, setTextValue] = useState<string>("");
    const [textTitle, setTextTitle] = useState<string | null>("");
    const [promptForRemix, setPromptForRemix] = useState<string | null>("");
    const [textRemix, setTextRemix] = useState<string | null>("");
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [languageCode, setLanguageCode] = useState<string | null>("");
    const [loadingTTSGeneration, setLoadingTTSGeneration] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [isGenerateButtonDisabled, setIsGenerateButtonDisabled] = useState<boolean>(true);
    const [fieldErrors, setFieldErrors] = useState<FieldErrorsRemix>({
        textTitle: false,
        textValue: false,
        promptForRemix: false,
    });
    const [waitingRank, setWaitingRank] = useState<number | null>(null);
    const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
    const [generationStatus, setGenerationStatus] = useState<"waiting" | "success" | "error">("waiting");
    const [errorReason, setErrorReason] = useState<"generic" | "quota">("generic");
    const { isTextExhausted, refetch: refetchTokens, data: tokenData } = useRemainingTokens();

    const handleGenerateRemix = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }

        if (!textTitle || !textValue || !promptForRemix || !selectedContentLanguage) {
            setShowErrors(true);
            setTimeout(() => {
                setShowErrors(false);
            }, 3000);
            return;
        }

        setLoadingTTSGeneration(true);
        setErrorReason("generic");

        try {
            const response = await createCartelRemixQueueAI({
                token: token,
                textTitle: textTitle,
                textObject: textValue,
                remixPrompt: promptForRemix,
                language: selectedContentLanguage,
            });

            setLoadingTTSGeneration(false);
            if (!response.id) {
                console.error("Error generating cartel remix");
                setShowStatusModal(true);
                setGenerationStatus("error");
            } else {
                const idRedirect = response.id;
                navigate("/create/cartel-remix/" + idRedirect);
            }
        } catch (err) {
            setLoadingTTSGeneration(false);
            if (isQuotaError(err)) {
                setErrorReason("quota");
                refetchTokens();
            }
            setShowStatusModal(true);
            setGenerationStatus("error");
        }
    };

    useEffect(() => {
        if (id && id.trim() !== "") {
            handleCartelAIAwaiting(id);
        } else {
            setTextTitle("");
            setTextValue("");
            setPromptForRemix("");
        }
        // fetch-on-mount when id changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleCartelAIAwaiting = async (id: string) => {
        setLoadingTTSGeneration(true);
        setShowStatusModal(true);
        setGenerationStatus("waiting");
        setWaitingRank(null);

        const fetchCartelAI = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    return;
                }

                const cartelAI = await getCartelAIWaiting({
                    token: token,
                    cartelAIId: id,
                });

                if (!cartelAI || !cartelAI.cartelAI) {
                    console.error("Unexpected response format:", cartelAI);
                    setLoadingTTSGeneration(false);
                    return;
                }

                if (cartelAI?.failed) {
                    setLoadingTTSGeneration(false);
                    setGenerationStatus("error");
                    return;
                }

                const { waiting, cartelAI: cartel, queueAI: queue } = cartelAI;

                if (queue && queue.queueAIRemixId && queue.queueAIRemix) {
                    setTextValue(queue.queueAIRemix.textObject);
                }

                const text = cartel?.text || "";

                if (text.trim() && !(queue && queue.queueAIRemixId)) {
                    setTextValue(text);
                    setTextRemix(text);
                    setShowStatusModal(false);
                } else if (text.trim() && queue && queue.queueAIRemixId) {
                    setTextRemix(text);
                }

                if (queue) {
                    if (cartel) {
                        setTextTitle(cartel.name);
                        const languageCode = cartel.language;
                        setLanguageCode(languageCode);
                    }

                    if (queue && queue.queueAIRemix) {
                        setPromptForRemix(queue.queueAIRemix.promptRemix);
                    }
                }

                if (waiting || (queue && !queue.queueAIRemixId)) {
                    if (queue && !queue.queueAIRemixId) setLoadingTTSGeneration(false);

                    if (queue && queue.queueAIRemixId) {
                        if (typeof cartelAI.rank === "number") {
                            setWaitingRank(cartelAI.rank);
                        }
                        setTimeout(fetchCartelAI, 2000);
                    }
                } else {
                    setGenerationStatus("success");
                    setLoadingTTSGeneration(false);
                    setWaitingRank(null);
                    refetchTokens();
                }
            } catch (error) {
                console.error("Error fetching cartel AI data:", error);
                setLoadingTTSGeneration(false);
                setGenerationStatus("error");
            }
        };

        fetchCartelAI();
    };

    const handleAddToProject = async (projectId: string, newProjectName: string) => {
        const token = localStorage.getItem("token");
        if (!token || !id) {
            return;
        }

        const cartelAI = await getCartelAIWaiting({
            token: token,
            cartelAIId: id,
        });

        if (cartelAI?.cartelAI) {
            if (projectId === "new") {
                await createProjectCartel({
                    token: token,
                    name: newProjectName,
                    cartelAIId: cartelAI.cartelAI.id,
                });
            } else {
                await addItemToProjectAI({
                    token: token,
                    projectId: projectId,
                    itemId: cartelAI.cartelAI.id,
                    itemType: AI_MODEL_TYPES.CARTEL,
                });
            }

            await refreshProjects();
        }
    };

    useEffect(() => {
        const errors: FieldErrorsRemix = {
            textTitle: !textTitle,
            textValue: !textValue,
            promptForRemix: !promptForRemix,
        };

        setFieldErrors(errors);

        const hasErrors = Object.values(errors).some((error) => error);
        setIsGenerateButtonDisabled(hasErrors);
    }, [textValue, textTitle, promptForRemix]);

    useEffect(() => {
        if (data && data.languagesAI && data.languagesAI.length > 0 && languageCode) {
            const languageAI = data.languagesAI;

            const item = languageAI.find((o) => o.code.toUpperCase() === languageCode.toUpperCase());

            const languageId = item?.value;

            if (languageId) {
                setSelectedContentLanguage(languageId);
            }
        }
    }, [languageCode, data]);

    const handleNextStep = () => {
        navigate(`/create/cartel-translation`);
    };

    const remainingWords = tokenData?.remainigTextTokens;

    return (
        <div className={styles.page}>
            <section className={styles.main}>
                <div className={styles.mainHead}>
                    <div>
                        <h1>{t("textRemix")}</h1>
                        <p>{t("writeTheStoryYouWanToRemix")}</p>
                    </div>
                    {remainingWords != null && (
                        <span className={styles.token}>
                            <Icon name="ic-sparkles" />
                            {t("remainingWords")}&nbsp;: <b>{remainingWords.toLocaleString()}</b>
                        </span>
                    )}
                </div>

                <div className={styles.stream}>
                    <div className={styles.streamInner}>
                        {!textRemix && !loadingTTSGeneration ? (
                            <div className={styles.empty}>
                                <div className={styles.emptyIco}>
                                    <Icon name="ic-shuffle" />
                                </div>
                                <h3>{t("yourRemixedTextWillAppearHere")}</h3>
                                <p>{t("writeTheStoryYouWanToRemix")}</p>
                            </div>
                        ) : (
                            <textarea
                                className={styles.result}
                                value={textRemix || ""}
                                onChange={(e) => setTextRemix(e.target.value)}
                                placeholder={
                                    loadingTTSGeneration ? t("generating") : t("yourRemixedTextWillAppearHere")
                                }
                            />
                        )}
                    </div>
                </div>

                <div className={styles.composerWrap}>
                    <div className={styles.composer}>
                        <div className={styles.titleRow}>
                            <input
                                className={`${styles.titleInput}${showErrors && fieldErrors.textTitle ? ` ${styles.missing}` : ""}`}
                                placeholder={t("title")}
                                value={textTitle || ""}
                                onChange={(e) => setTextTitle(e.target.value)}
                            />
                        </div>
                        <textarea
                            className={`${styles.fieldSrc}${showErrors && fieldErrors.textValue ? ` ${styles.missing}` : ""}`}
                            placeholder={t("typeYourTextHere") || t("enterTextToRemix")}
                            value={textValue}
                            onChange={(e) => setTextValue(e.target.value)}
                        />
                        <textarea
                            className={`${styles.fieldPrompt}${showErrors && fieldErrors.promptForRemix ? ` ${styles.missing}` : ""}`}
                            placeholder={t("placeholderTextRemix") || t("promptForRemix")}
                            value={promptForRemix || ""}
                            onChange={(e) => setPromptForRemix(e.target.value)}
                        />
                        <div className={styles.composerBar}>
                            <div className={styles.tool}>
                                <Icon name="ic-languages" />
                                <select
                                    className={styles.langSelect}
                                    value={selectedContentLanguage || ""}
                                    onChange={(e) => setSelectedContentLanguage(e.target.value || null)}
                                >
                                    <option value="">{t("selectALanguage")}</option>
                                    {(data?.languagesAI || []).map((lang: any) => (
                                        <option key={lang.value} value={lang.value}>
                                            {lang.label || lang.name || lang.code}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className={styles.spacer} />
                            <Tooltip title={isTextExhausted ? t("quotaReachedTextTooltip") : ""} placement="top">
                                <span>
                                    <button
                                        type="button"
                                        className={styles.send}
                                        title={t("generateCartelRemix")}
                                        onClick={handleGenerateRemix}
                                        disabled={isGenerateButtonDisabled || loadingTTSGeneration || isTextExhausted}
                                    >
                                        <Icon name="ic-arrow-right" />
                                    </button>
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                    <div className={styles.hint}>{t("remixComposerHint")}</div>
                    <div className={styles.actions}>
                        {textRemix && <Button label={t("nextStep")} onClick={handleNextStep} variant="ghost" />}
                        {textRemix && textRemix !== "" && textTitle && textTitle !== "" && (
                            <AddToProjectButton onClick={() => setIsModalOpen(true)} />
                        )}
                    </div>
                </div>
            </section>

            <AddCartelToProjectModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAddToProject={handleAddToProject}
                placeholder={t("addCartelToProject")}
            />

            <AIGenerationStatusModal
                isOpen={showStatusModal}
                onClose={() => setShowStatusModal(false)}
                status={generationStatus}
                rank={waitingRank}
                errorTitle={errorReason === "quota" ? t("quotaReachedTitle") : undefined}
                errorSubtitle={errorReason === "quota" ? t("quotaReachedTextSubtitle") : undefined}
            />
        </div>
    );
};

export default CartelRemix;
